import { ChangeDetectorRef, Component, ComponentRef, ElementRef, HostListener, Input, ViewChild, ViewContainerRef } from "@angular/core";
import { Deck, DeckChanges, GameType, UpdateDeckRequest, UpdateDeckResponse, VisibilityType } from "@apiserver-rest/ponder-data-client-base";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { take } from "rxjs";
import { DeckTypes } from "@ponder-shared/utils/deckTypes";
import { VisibilityTypes } from "@ponder-shared/utils/visbilityTypes";
import { DataBroker } from "@ponder-core/data/dataBroker";
import { keepInBoundsX } from "@ponder-shared/utils/keepInBounds";

@Component({
    selector: "deck-edit-options",
    imports: [],
    templateUrl: "./deck-edit-options.component.html"
})
export class DeckEditOptionsComponent {
    @ViewChild("dialog", { static: false }) public dialogRef?: ElementRef<HTMLDivElement>;
    @Input({ required: true }) public deck!: Deck;

    @ViewChild("deleteConfirmationContainer", { read: ViewContainerRef }) private deleteConfirmationVcr!: ViewContainerRef;
    
    @ViewChild("nameChangeInput") private nameChangeInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild("groupChangeInput") private groupChangeInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild("deckTypeSelect") private deckTypeSelectRef!: ElementRef<HTMLSelectElement>;
    @ViewChild("visibilitySelect") private visibilitySelectRef!: ElementRef<HTMLSelectElement>;
    @ViewChild("colorChangeInput") private colorChangeInputRef!: ElementRef<HTMLSelectElement>;

    public readonly editSvgIcon: string = SvgIconMap.get(SvgIcon.EditAlt)!;
    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    public readonly undoSvgIcon: string = SvgIconMap.get(SvgIcon.Undo)!;
    public readonly exportSvgIcon: string = SvgIconMap.get(SvgIcon.Export)!;
    public readonly deleteSvgIcon: string = SvgIconMap.get(SvgIcon.Delete)!;
    public readonly deckTypes = DeckTypes.get(GameType.Magic)!;
    public readonly visibilityTypes = VisibilityTypes;

    public displayOptions: boolean = false;
    public deckPreviewColor!: string;
    public colorHistory: string[] = [];
    public isUpdating: boolean = false;
    public deckGroups: string[] = [];

    private prevProperties!: DeckEditProperties;

    public constructor(
        private elementRef: ElementRef, 
        private ponderDataClient: PonderDataClient, 
        private events: EventBroker,
        private data: DataBroker,
        private changeDetector: ChangeDetectorRef) {
    }

    public ngOnInit(): void {
        this.data.deck.requestDeckGroups.emit((groups: string[]) => {
            this.deckGroups = groups;
            // ^ todo this doesn't reflect new/changed group names
        });
    }

    private onShow(): void {
        this.prevProperties = {
            name: this.deck.name!,
            group: this.deck.group!,
            type: this.deck.type!,
            visibilty: this.deck.visibility as string,
            color: this.deck.color!
        };
        
        this.deckPreviewColor = this.deck.color!;
        this.displayOptions = true;
    }

    private onHide(): void {
        this.displayOptions = false;
    }

    private onCancel(): void {
        if (this.wasModified()) {
            this.deck.name = this.prevProperties.name;
            this.deck.group = this.prevProperties.group;
            this.deck.type = this.prevProperties.type;
            this.deck.visibility = this.prevProperties.visibilty as VisibilityType;
            this.deck.color = this.prevProperties.color;
    
            this.events.deck.deckBackgroundUpdatedEvent.emit(this.deck);
        }

        this.colorHistory = [];

        this.onHide();
    }

    public onEditDeckClicked(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        
        if (this.displayOptions) {
            this.onCancel();
        } else {
            this.onShow();
        }
        
        if (!this.dialogRef) {
            this.changeDetector.detectChanges();
        }
        
        if (this.dialogRef) {
            keepInBoundsX(this.dialogRef.nativeElement, 10);
        }
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onCancel();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.displayOptions && !this.elementRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.displayOptions && this.mouseDownOutside && !this.elementRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onCancel();
        }

        this.mouseDownOutside = false;
    }

    // -------------------------------------------------------------------------------------
    // Color Change
    // -------------------------------------------------------------------------------------

    public onColorChange(value: string): void {
        this.deckPreviewColor = value;
    }

    public onColorSet(value: string): void {
        this.colorHistory.push(this.deck.color!);
        this.deckPreviewColor = value;
        this.deck.color = value;

        this.events.deck.deckBackgroundUpdatedEvent.emit(this.deck);
    }

    public onUndoColorClicked(): void {
        var prevColor = this.colorHistory.pop();

        if (prevColor) {
            this.deckPreviewColor = prevColor;
            this.deck.color = prevColor;
            this.colorChangeInputRef.nativeElement.value = prevColor;

            this.events.deck.deckBackgroundUpdatedEvent.emit(this.deck);
        }
    }
    
    // -------------------------------------------------------------------------------------
    // On Cancel
    // -------------------------------------------------------------------------------------

    public onCancelClicked(): void {
        this.onCancel();
    }
    
    // -------------------------------------------------------------------------------------
    // On Save
    // -------------------------------------------------------------------------------------

    public onSaveClicked(): void {
        if (this.isUpdating) {
            return;
        }

        if (!this.wasModified()) {
            this.onHide();
            return;
        }

        this.isUpdating = true;

        this.deck.name = this.nameChangeInputRef.nativeElement.value;
        this.deck.group = this.groupChangeInputRef.nativeElement.value;
        this.deck.type = this.deckTypeSelectRef.nativeElement.value;
        this.deck.visibility = this.visibilitySelectRef.nativeElement.value as VisibilityType;
        this.deck.color = this.colorChangeInputRef.nativeElement.value;

        this.events.deck.deckBackgroundUpdatedEvent.emit(this.deck);

        var updateRequest = new UpdateDeckRequest({
            deckId: this.deck.id,
            deckChanges: new DeckChanges({
                name: this.deck.name,
                group: this.deck.group,
                type: this.deck.type,
                visibility: this.deck.visibility,
                color: this.deck.color
            })
        });

        this.ponderDataClient
            .deckUpdate(updateRequest)
            .pipe(take(1))
            .subscribe({
                next: (response: UpdateDeckResponse) => {
                    if (response.success) {
                        if (this.prevProperties.group != this.deck.group) {
                            this.events.deck.deckChangeGroupEvent.emit({
                                deck: this.deck,
                                fromGroup: this.prevProperties.group,
                                toGroup: this.deck.group!
                            });
                        }
                    } else {
                        this.onCancel();
                    }
                },
                error: () => {
                    this.onCancel();
                }
            })
            .add(() => { 
                this.isUpdating = false; 
            });

        this.onHide(); 
    }

    private wasModified(): boolean {
        if ((this.deck.name != this.nameChangeInputRef.nativeElement.value) ||
            (this.deck.group != this.groupChangeInputRef.nativeElement.value) ||
            (this.deck.type != this.deckTypeSelectRef.nativeElement.value) ||
            (this.deck.visibility != this.visibilitySelectRef.nativeElement.value) ||
            this.colorHistory.length) {
            return true;
        }

        return false;
    }
}

type DeckEditProperties = {
    name: string;
    group: string;
    type: string;
    visibilty: string;
    color: string;
}